/**
 * 빠른 입력 한국어 자연어 파서 (PRD 6.4). 서버와 클라이언트가 같은 모듈을 쓴다.
 *
 *   "내일 오후 3시 견적서 보내기 #영업 @minsu +급함 !1"
 *
 * 토큰은 낱말 경계(앞이 공백이나 문장 시작, 뒤가 공백이나 끝)에서만 인식한다.
 * 날짜·시각 뒤에는 "까지", "에" 같은 조사가 붙어도 된다. 인식한 토큰은 제목에서 뺀다.
 */
import { addDays, addMonths, type DateStr, dateParts, daysInMonth, lastDayOfMonth, makeDate, minutesToTime, mondayOf, timeToMinutes, weekday } from "./dates";
import { firstOccurrence, formatRRule, type Recurrence } from "./recur";

export type QuickTokenKind = "date" | "time" | "recur" | "target" | "assignee" | "label" | "priority";

export interface QuickToken {
  kind: QuickTokenKind;
  /** 원문에서의 위치 [start, end) */
  start: number;
  end: number;
  text: string;
}

export interface QuickAddOptions {
  today: DateStr;
  /** 지금 시각 (하루 중 분). 날짜 없이 시각만 썼는데 이미 지났으면 내일로 */
  nowMinutes?: number;
  /** 사용자가 해제한 토큰의 시작 위치 */
  ignore?: readonly number[];
  /** #이름이 목록이나 채널인지 (없으면 모두 인정) */
  isTarget?: (name: string) => boolean;
  /** @이름이 멤버인지 (없으면 모두 인정) */
  isUser?: (name: string) => boolean;
}

export interface QuickAddResult {
  title: string;
  tokens: QuickToken[];
  dueDate: DateStr | null;
  dueTime: string | null;
  rrule: string | null;
  repeatFromCompletion: boolean;
  /** #이름 (목록인지 채널인지는 부르는 쪽이 판단) */
  target: string | null;
  assignee: string | null;
  labels: string[];
  priority: number | null;
}

/* ───────────── 공통 조각 ───────────── */

const DAYS = "월화수목금토일";
const dayIndex = (ch: string) => DAYS.indexOf(ch);

/** 날짜·시각 뒤에 붙어도 되는 조사 */
const DATE_SUFFIX = "(?:까지는|까지|에는|에|엔|부터)?";
const TIME_SUFFIX = "(?:까지|에|부터|쯤)?";
const END = "(?=$|\\s)";

const NUMBER_WORDS: Record<string, number> = { 한: 1, 두: 2, 세: 3, 네: 4, 다섯: 5, 여섯: 6, 일곱: 7, 여덟: 8, 아홉: 9, 열: 10 };
const NUM = "(\\d{1,3}|한|두|세|네|다섯|여섯|일곱|여덟|아홉|열)";
const toNumber = (s: string) => NUMBER_WORDS[s] ?? Number(s);

const NTH: Record<string, number> = { 첫: 1, 둘: 2, 두: 2, 셋: 3, 세: 3, 넷: 4, 네: 4, 마지막: -1 };

interface Matcher<T> {
  re: RegExp;
  build: (m: RegExpExecArray, ctx: QuickAddOptions) => T | null;
}

const rx = (body: string, suffix = "") => new RegExp(body + suffix + END, "y");

/* ───────────── 반복 ───────────── */

interface RecurValue {
  rule: Recurrence;
  /** 시작 날짜에 맞춰 요일·날짜를 채워야 하는 규칙 ("매주" → 오늘 요일) */
  fill?: "weekday" | "monthday" | "yearday";
}

const COMPLETION = "(\\s?\\(?\\s?완료\\s?(?:후|기준)\\s?\\)?)?";

const recurMatchers: Matcher<RecurValue>[] = [
  { re: rx("매일" + COMPLETION), build: () => ({ rule: { freq: "DAILY", interval: 1 } }) },
  { re: rx("(?:평일마다|매\\s?평일)" + COMPLETION), build: () => ({ rule: { freq: "WEEKLY", interval: 1, byDay: [0, 1, 2, 3, 4].map((day) => ({ day })) } }) },
  { re: rx("(?:주말마다|매\\s?주말)" + COMPLETION), build: () => ({ rule: { freq: "WEEKLY", interval: 1, byDay: [{ day: 5 }, { day: 6 }] } }) },
  {
    re: rx(`(매주|격주)(?:\\s?([${DAYS}](?:[,·/]?[${DAYS}])*)(?:요일)?)?` + COMPLETION),
    build: (m) => {
      const interval = m[1] === "격주" ? 2 : 1;
      if (!m[2]) return { rule: { freq: "WEEKLY", interval }, fill: "weekday" };
      const days = [...new Set([...m[2].replace(/[,·/]/g, "")].map(dayIndex))].sort();
      return { rule: { freq: "WEEKLY", interval, byDay: days.map((day) => ({ day })) } };
    },
  },
  {
    re: rx(`(?:매달|매월)(?:\\s?(?:(\\d{1,2})일|(마지막\\s?날|말일|말)|(첫|둘|두|셋|세|넷|네|마지막)\\s?(?:째|번째)?\\s?([${DAYS}])요일))?` + COMPLETION),
    build: (m) => {
      if (m[1]) {
        const d = Number(m[1]);
        return d >= 1 && d <= 31 ? { rule: { freq: "MONTHLY", interval: 1, byMonthDay: d } } : null;
      }
      if (m[2]) return { rule: { freq: "MONTHLY", interval: 1, byMonthDay: -1 } };
      if (m[3] && m[4]) return { rule: { freq: "MONTHLY", interval: 1, byDay: [{ day: dayIndex(m[4]), nth: NTH[m[3]]! }] } };
      return { rule: { freq: "MONTHLY", interval: 1 }, fill: "monthday" };
    },
  },
  {
    re: rx("(?:매년|매해)(?:\\s?(\\d{1,2})월\\s?(\\d{1,2})일)?" + COMPLETION),
    build: (m) => {
      if (!m[1]) return { rule: { freq: "YEARLY", interval: 1 }, fill: "yearday" };
      const month = Number(m[1]);
      const day = Number(m[2]);
      if (month < 1 || month > 12 || day < 1 || day > daysInMonth(2024, month)) return null;
      return { rule: { freq: "YEARLY", interval: 1, byMonth: month, byMonthDay: day } };
    },
  },
  {
    // 매 3일, 매 2주, 3일마다, 2주마다, 매 3일(완료 후)
    re: rx("(?:매\\s?(\\d{1,3})\\s?(일|주|달|개월|년)|(\\d{1,3})\\s?(일|주|달|개월|년)\\s?마다)" + COMPLETION),
    build: (m) => {
      const n = Number(m[1] ?? m[3]);
      const unit = m[2] ?? m[4];
      if (n < 1 || n > 365) return null;
      const freq = unit === "일" ? "DAILY" : unit === "주" ? "WEEKLY" : unit === "년" ? "YEARLY" : "MONTHLY";
      return { rule: { freq, interval: n }, fill: freq === "WEEKLY" ? "weekday" : freq === "MONTHLY" ? "monthday" : freq === "YEARLY" ? "yearday" : undefined };
    },
  },
];

/* ───────────── 날짜 ───────────── */

/** 이번 주(월요일 시작)의 토요일. 오늘이 주말이면 오늘 */
function weekend(today: DateStr, weeks: number): DateStr {
  if (weeks === 0 && weekday(today) >= 5) return today;
  return addDays(mondayOf(today), 7 * weeks + 5);
}

/** 이번 달 d일, 지났거나 없는 날이면 그다음 달부터 그날이 있는 달 */
function nextMonthDay(today: DateStr, d: number): DateStr | null {
  if (d < 1 || d > 31) return null;
  const { year, month } = dateParts(today);
  for (let i = 0; i < 3; i++) {
    const y = year + Math.floor((month - 1 + i) / 12);
    const mo = ((month - 1 + i) % 12) + 1;
    if (d > daysInMonth(y, mo)) continue;
    const candidate = makeDate(y, mo, d);
    if (candidate >= today) return candidate;
  }
  return null;
}

/** 올해 M월 D일, 지났으면 내년 */
function nextMonthAndDay(today: DateStr, month: number, day: number): DateStr | null {
  if (month < 1 || month > 12 || day < 1) return null;
  const { year } = dateParts(today);
  for (const y of [year, year + 1, year + 2, year + 3, year + 4]) {
    if (day > daysInMonth(y, month)) continue;
    const candidate = makeDate(y, month, day);
    if (candidate >= today) return candidate;
  }
  return null;
}

const WEEK_OFFSET: Record<string, number> = { 이번: 0, 다음: 1, 담: 1, 다다음: 2 };
const RELATIVE_WORDS: Record<string, number> = { 하루: 1, 이틀: 2, 사흘: 3, 나흘: 4, 일주일: 7, 열흘: 10 };

const dateMatchers: Matcher<DateStr>[] = [
  { re: rx("(오늘|금일|내일|모레|글피)", DATE_SUFFIX), build: (m, { today }) => addDays(today, { 오늘: 0, 금일: 0, 내일: 1, 모레: 2, 글피: 3 }[m[1]!]!) },
  { re: rx("(이번|다음|다다음|담)\\s?주\\s?말", DATE_SUFFIX), build: (m, { today }) => weekend(today, WEEK_OFFSET[m[1]!]!) },
  {
    re: rx(`(이번|다음|다다음|담)\\s?주\\s?([${DAYS}])요일`, DATE_SUFFIX),
    build: (m, { today }) => addDays(mondayOf(today), 7 * WEEK_OFFSET[m[1]!]! + dayIndex(m[2]!)),
  },
  {
    // 오늘 이후 가장 가까운 그 요일 (오늘이 그 요일이면 다음 주)
    re: rx(`([${DAYS}])요일`, DATE_SUFFIX),
    build: (m, { today }) => addDays(today, ((dayIndex(m[1]!) - weekday(today) + 6) % 7) + 1),
  },
  {
    re: rx("(이번|다음|다다음)\\s?달\\s?(?:(\\d{1,2})일|(말일?|마지막\\s?날))", DATE_SUFFIX),
    build: (m, { today }) => {
      const base = addMonths(makeDate(dateParts(today).year, dateParts(today).month, 1), { 이번: 0, 다음: 1, 다다음: 2 }[m[1]!]!);
      if (m[3]) return lastDayOfMonth(base);
      const { year, month } = dateParts(base);
      const d = Number(m[2]);
      return d >= 1 && d <= daysInMonth(year, month) ? makeDate(year, month, d) : null;
    },
  },
  { re: rx("월말", DATE_SUFFIX), build: (_m, { today }) => lastDayOfMonth(today) },
  {
    re: rx("(\\d{4})[-./](\\d{1,2})[-./](\\d{1,2})", DATE_SUFFIX),
    build: (m) => {
      const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
      return mo >= 1 && mo <= 12 && d >= 1 && d <= daysInMonth(y, mo) ? makeDate(y, mo, d) : null;
    },
  },
  { re: rx("(\\d{1,2})\\/(\\d{1,2})", DATE_SUFFIX), build: (m, { today }) => nextMonthAndDay(today, Number(m[1]), Number(m[2])) },
  { re: rx("(\\d{1,2})월\\s?(\\d{1,2})일", DATE_SUFFIX), build: (m, { today }) => nextMonthAndDay(today, Number(m[1]), Number(m[2])) },
  {
    re: rx(`${NUM}\\s?(일|주일?|달|개월|년)\\s?(?:후|뒤)`, DATE_SUFFIX),
    build: (m, { today }) => {
      const n = toNumber(m[1]!);
      if (!n) return null;
      const unit = m[2]!;
      if (unit === "일") return addDays(today, n);
      if (unit.startsWith("주")) return addDays(today, 7 * n);
      if (unit === "년") return addMonths(today, 12 * n);
      return addMonths(today, n);
    },
  },
  { re: rx("(하루|이틀|사흘|나흘|일주일|열흘)\\s?(?:후|뒤)", DATE_SUFFIX), build: (m, { today }) => addDays(today, RELATIVE_WORDS[m[1]!]!) },
  { re: rx("(\\d{1,2})일", DATE_SUFFIX), build: (m, { today }) => nextMonthDay(today, Number(m[1])) },
];

/* ───────────── 시각 ───────────── */

/** 시각 + 하루 넘김 ("밤 12시" = 다음 날 0시) */
interface TimeValue {
  minutes: number;
  nextDay?: boolean;
}

const PERIOD_DEFAULT: Record<string, number> = { 아침: 9 * 60, 점심: 12 * 60, 정오: 12 * 60, 저녁: 19 * 60, 밤: 21 * 60 };

/** 오전·오후 같은 말과 시를 24시간제로 (PRD 6.4 해석 규칙) */
function toHour(period: string | undefined, h: number): number | null {
  switch (period) {
    case "오전":
      return h >= 1 && h <= 12 ? h % 12 : null;
    case "오후":
      // "오후 13시"처럼 24시간제로 써도 받아준다
      return h >= 1 && h <= 12 ? (h % 12) + 12 : h >= 13 && h <= 23 ? h : null;
    case "새벽":
    case "아침":
      return h >= 0 && h <= 11 ? h : h === 12 ? 0 : null;
    case "낮":
    case "점심":
      return h >= 1 && h <= 5 ? h + 12 : h >= 11 && h <= 12 ? h : null;
    case "저녁":
    case "밤":
      return h >= 1 && h <= 11 ? h + 12 : h === 12 ? 24 : null;
    default:
      if (h >= 1 && h <= 7) return h + 12;
      if (h >= 8 && h <= 12) return h;
      return h === 0 || (h >= 13 && h <= 23) ? h : null;
  }
}

function timeValue(hour: number | null, minute: number): TimeValue | null {
  if (hour === null || minute < 0 || minute > 59) return null;
  if (hour === 24) return minute === 0 ? { minutes: 0, nextDay: true } : null;
  return { minutes: hour * 60 + minute };
}

const timeMatchers: Matcher<TimeValue>[] = [
  {
    re: rx("(?:(오전|오후|새벽|아침|낮|점심|저녁|밤)\\s?)?(\\d{1,2})시(?:\\s?(반)|\\s?(\\d{1,2})분)?", TIME_SUFFIX),
    build: (m) => timeValue(toHour(m[1], Number(m[2])), m[3] ? 30 : m[4] ? Number(m[4]) : 0),
  },
  {
    re: rx("(?:(오전|오후)\\s?)?(\\d{1,2}):(\\d{2})", TIME_SUFFIX),
    build: (m) => {
      const h = Number(m[2]);
      const hour = m[1] ? toHour(m[1], h) : h <= 23 ? h : null;
      return timeValue(hour, Number(m[3]));
    },
  },
  { re: rx("(아침|점심|정오|저녁|밤)", TIME_SUFFIX), build: (m) => ({ minutes: PERIOD_DEFAULT[m[1]!]! }) },
];

/* ───────────── 기호 토큰 ───────────── */

const NAME = "([^\\s#@+!]+)";
const sigilMatchers: { kind: "target" | "assignee" | "label" | "priority"; re: RegExp; accept?: (name: string, ctx: QuickAddOptions) => boolean }[] = [
  // #T-123은 할일 참조이므로 목록·채널로 보지 않는다
  { kind: "target", re: rx("#" + NAME), accept: (name, ctx) => !/^T-\d+$/i.test(name) && (ctx.isTarget?.(name) ?? true) },
  { kind: "assignee", re: rx("@" + NAME), accept: (name, ctx) => ctx.isUser?.(name) ?? true },
  { kind: "label", re: rx("\\+" + NAME) },
  { kind: "priority", re: rx("!([1-4])") },
];

/* ───────────── 파서 ───────────── */

export function parseQuickAdd(text: string, options: QuickAddOptions): QuickAddResult {
  const ignore = new Set(options.ignore ?? []);
  const tokens: QuickToken[] = [];
  let date: DateStr | null = null;
  let time: TimeValue | null = null;
  let recur: (RecurValue & { fromCompletion: boolean }) | null = null;
  let target: string | null = null;
  let assignee: string | null = null;
  let priority: number | null = null;
  const labels: string[] = [];

  const tryAt = <T>(matchers: Matcher<T>[], i: number): { value: T; end: number } | null => {
    for (const { re, build } of matchers) {
      re.lastIndex = i;
      const m = re.exec(text);
      if (!m) continue;
      const value = build(m, options);
      if (value !== null) return { value, end: i + m[0].length };
    }
    return null;
  };

  let i = 0;
  while (i < text.length) {
    if (/\s/.test(text[i]!) || (i > 0 && !/\s/.test(text[i - 1]!))) {
      i++;
      continue;
    }
    let hit: { kind: QuickTokenKind; end: number; apply: () => void } | null = null;

    const r = !recur ? tryAt(recurMatchers, i) : null;
    if (r) {
      const fromCompletion = /완료/.test(text.slice(i, r.end));
      hit = { kind: "recur", end: r.end, apply: () => (recur = { ...r.value, fromCompletion }) };
    }
    const d = !hit && !date ? tryAt(dateMatchers, i) : null;
    if (d) hit = { kind: "date", end: d.end, apply: () => (date = d.value) };
    const t = !hit && !time ? tryAt(timeMatchers, i) : null;
    if (t) hit = { kind: "time", end: t.end, apply: () => (time = t.value) };
    if (!hit) {
      for (const s of sigilMatchers) {
        s.re.lastIndex = i;
        const m = s.re.exec(text);
        if (!m) continue;
        const value = m[1]!;
        if (s.accept && !s.accept(value, options)) continue;
        const end = i + m[0].length;
        if (s.kind === "target" && !target) hit = { kind: "target", end, apply: () => (target = value) };
        else if (s.kind === "assignee" && !assignee) hit = { kind: "assignee", end, apply: () => (assignee = value) };
        else if (s.kind === "priority" && priority === null) hit = { kind: "priority", end, apply: () => (priority = Number(value)) };
        else if (s.kind === "label" && !labels.includes(value)) hit = { kind: "label", end, apply: () => labels.push(value) };
        break;
      }
    }

    if (!hit) {
      i++;
      continue;
    }
    // 해제한 토큰은 글자로 남기고, 그 안의 낱말을 다시 토큰으로 보지 않는다
    if (!ignore.has(i)) {
      hit.apply();
      tokens.push({ kind: hit.kind, start: i, end: hit.end, text: text.slice(i, hit.end) });
    }
    i = hit.end;
  }

  // 마감 날짜·시각
  const t = time as TimeValue | null;
  let dueDate: DateStr | null = date;
  if (!dueDate && t) {
    const passed = options.nowMinutes !== undefined && !t.nextDay && t.minutes <= options.nowMinutes;
    dueDate = passed ? addDays(options.today, 1) : options.today;
  }
  if (dueDate && t?.nextDay) dueDate = addDays(dueDate, 1);
  const dueTime = t ? minutesToTime(t.minutes) : null;

  // 반복: 시작 날짜(지정한 날짜 또는 오늘)부터 첫 회차
  let rrule: string | null = null;
  const rc = recur as (RecurValue & { fromCompletion: boolean }) | null;
  if (rc) {
    const start = dueDate ?? options.today;
    const rule: Recurrence = { ...rc.rule };
    if (rc.fill === "weekday") rule.byDay = [{ day: weekday(start) }];
    if (rc.fill === "monthday") rule.byMonthDay = dateParts(start).day;
    if (rc.fill === "yearday") Object.assign(rule, { byMonth: dateParts(start).month, byMonthDay: dateParts(start).day });
    let first = firstOccurrence(rule, start, start);
    // 오늘 회차인데 시각이 이미 지났으면 다음 회차부터
    if (first === options.today && !date && dueTime && options.nowMinutes !== undefined && timeToMinutes(dueTime) <= options.nowMinutes) {
      first = firstOccurrence(rule, addDays(options.today, 1), start);
    }
    if (first) {
      dueDate = first;
      rrule = formatRRule(rule);
    }
  }

  return {
    title: removeTokens(text, tokens),
    tokens,
    dueDate,
    dueTime,
    rrule,
    repeatFromCompletion: !!rc?.fromCompletion && !!rrule,
    target,
    assignee,
    labels,
    priority,
  };
}

function removeTokens(text: string, tokens: QuickToken[]): string {
  let out = "";
  let at = 0;
  for (const tk of [...tokens].sort((a, b) => a.start - b.start)) {
    out += text.slice(at, tk.start) + " ";
    at = tk.end;
  }
  out += text.slice(at);
  return out.replace(/\s+/g, " ").trim();
}
